import React, { useState, useEffect } from "react";
import { X, Upload, Package, Layers, Search, Check, Trash2, Plus, Image as ImageIcon } from "lucide-react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const AddItemModal = ({ isOpen, onClose, onAddItem, editDish }) => {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    discounted_price: "",
    image_url: "",
  });
  const [showStockModal, setShowStockModal] = useState(false);

  useEffect(() => {
    if (editDish) {
      setFormData({
        name: editDish.name || "",
        description: editDish.description || "",
        price: editDish.price?.toString() || "",
        discounted_price: editDish.discounted_price?.toString() || "",
        image_url: Array.isArray(editDish.image_url) ? editDish.image_url.join(",") : (editDish.image_url || ""),
      });
    } else {
      setFormData({
        name: "",
        description: "",
        price: "",
        discounted_price: "",
        image_url: "",
      });
    }
  }, [editDish, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const uploadFile = async (file) => {
    const data = new FormData();
    data.append("file", file);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/upload/single`,
        data,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data.fileUrl;
    } catch (error) {
      toast.error("Error uploading file");
      return null;
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setIsUploading(true);
    try {
      const uploadPromises = files.map(uploadFile);
      const urls = await Promise.all(uploadPromises);
      const validUrls = urls.filter(url => url !== null);
      setFormData(prev => ({
        ...prev,
        image_url: prev.image_url ? `${prev.image_url},${validUrls.join(",")}` : validUrls.join(","),
      }));
      toast.success(`${validUrls.length} images uploaded!`);
    } catch (error) {
      toast.error("Error uploading images");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (idx) => {
    setFormData(prev => {
      let urls = [];
      if (Array.isArray(prev.image_url)) {
        urls = [...prev.image_url];
      } else if (typeof prev.image_url === 'string') {
        urls = prev.image_url.split(",").filter(Boolean);
      }
      urls.splice(idx, 1);
      return { ...prev, image_url: urls.join(",") };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const roleid = localStorage.getItem("roleid");
      const token = localStorage.getItem(`token_partner_shop`);
      if (!token) { navigate("/login"); return; }

      const requestBody = {
        ...formData,
        price: Number(formData.price),
        discounted_price: Number(formData.discounted_price || 0),
        shop_id: roleid,
      };

      const response = editDish
        ? await axios.put(`${process.env.REACT_APP_BASE_URL}/product/${editDish._id}`, requestBody, {
          headers: { Authorization: `Bearer ${token}` }
        })
        : await axios.post(`${process.env.REACT_APP_BASE_URL}/product`, requestBody, {
          headers: { Authorization: `Bearer ${token}` }
        });

      onAddItem(response.data);
      onClose();
      toast.success(editDish ? "Product updated successfully!" : "Product listed successfully!");
    } catch (error) {
       toast.error("Error saving product details");
    }
  };

  if (!isOpen) return null;

  const StockImagesModal = () => {
    const [stockImages, setStockImages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [loading, setLoading] = useState(false);
    const [filteredImages, setFilteredImages] = useState([]);

    useEffect(() => {
      if (showStockModal) fetchStockData();
    }, [showStockModal]);

    useEffect(() => {
      let filtered = stockImages;
      if (searchTerm) filtered = filtered.filter(it => it.item_name?.toLowerCase().includes(searchTerm.toLowerCase()));
      if (selectedCategory) filtered = filtered.filter(it => it.category === selectedCategory);
      setFilteredImages(filtered);
    }, [stockImages, searchTerm, selectedCategory]);

    const fetchStockData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem(`token_partner_shop`);
        const [imgs, cats] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BASE_URL}/superadmin/database/item`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${process.env.REACT_APP_BASE_URL}/superadmin/category`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setStockImages(imgs.data?.data || imgs.data || []);
        setCategories(cats.data?.data || []);
      } catch (err) { toast.error("Failed to load catalog"); }
      finally { setLoading(false); }
    };

    if (!showStockModal) return null;
    return (
      <div className="fixed inset-0 z-[160] flex items-start justify-center pt-20 px-4 pb-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300 overflow-y-auto">
        <div className="bg-white rounded-[3rem] w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-white/20">
          <div className="p-8 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <div>
              <h2 className="text-2xl font-black text-gray-900 tracking-tight">Product Image Library</h2>
              <p className="text-xs text-gray-500 font-medium mt-1">Choose a professional shot from our marketplace library.</p>
            </div>
            <button onClick={() => setShowStockModal(false)} className="p-3 bg-white hover:bg-rose-50 hover:text-rose-500 rounded-2xl transition-all border border-gray-50 shadow-sm">
              <X size={20} />
            </button>
          </div>
          <div className="p-8 border-b border-gray-100 bg-white flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors" size={20} />
              <input type="text" placeholder="Search by product name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border-none rounded-2xl focus:ring-2 focus:ring-primary/20 transition-all font-medium" />
            </div>
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="px-6 py-4 bg-gray-50/50 border-none rounded-2xl focus:ring-2 focus:ring-primary/20 transition-all font-bold text-gray-600 min-w-[200px]">
              <option value="">All Categories</option>
              {categories.map((c, i) => <option key={i} value={c.title || c}>{c.title || c}</option>)}
            </select>
          </div>
          <div className="flex-1 overflow-y-auto p-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 bg-gray-50/30">
            {loading ? (
              <div className="col-span-full py-20 text-center uppercase">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto" />
                <p className="text-[10px] font-black text-gray-400 tracking-widest mt-4">Syncing Catalog...</p>
              </div>
            ) : filteredImages.length === 0 ? (
              <div className="col-span-full py-20 text-center uppercase">
                <div className="p-6 bg-gray-100 rounded-full w-fit mx-auto text-gray-400 mb-4"><ImageIcon size={40} /></div>
                <p className="text-[10px] font-black text-gray-400 tracking-widest">No matching products found</p>
              </div>
            ) : filteredImages.map((it, i) => (
              <div key={i} className="group relative bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100">
                <div className="aspect-square overflow-hidden">
                  <img src={it.image_url} alt={it.item_name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                </div>
                <div className="p-4">
                  <p className="text-xs font-black text-gray-900 truncate mb-3 uppercase">{it.item_name}</p>
                  <button onClick={() => { setFormData(p => ({ ...p, image_url: p.image_url ? `${p.image_url},${it.image_url}` : it.image_url })); toast.success("Image selected!"); setShowStockModal(false); }} className="w-full py-2.5 bg-primary/5 text-primary text-[10px] font-black rounded-xl group-hover:bg-primary group-hover:text-white transition-all shadow-sm uppercase tracking-widest">Pick Image</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const content = (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md animate-in fade-in duration-300 overflow-y-auto">
      <div className="bg-white rounded-[2.5rem] w-full max-w-2xl shadow-2xl overflow-hidden border border-white/20 animate-in zoom-in-95 duration-500 flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-emerald-500/20">
              {editDish ? <Layers size={20} /> : <Plus size={20} />}
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900 tracking-tight leading-tight">
                {editDish ? "Modify Product" : "List New Product"}
              </h2>
              <p className="text-[10px] text-gray-500 font-medium mt-0.5">Update your travel storefront catalog.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-rose-50 hover:text-rose-500 rounded-xl transition-all border border-gray-100 bg-white">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Product Title</label>
                <div className="relative group">
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-emerald-500 transition-colors" size={16} />
                  <input name="name" type="text" value={formData.name} onChange={handleChange} placeholder="e.g. Travel Organizer Pro" className="w-full pl-10 pr-4 py-3 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all font-bold text-gray-900 outline-none uppercase text-[10px]" required />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Base Price (₹)</label>
                <div className="relative group">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-black text-gray-300 group-focus-within:text-emerald-500 transition-colors text-xs">₹</span>
                  <input name="price" type="number" value={formData.price} onChange={handleChange} placeholder="0" className="w-full pl-8 pr-4 py-3 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all font-black text-gray-900 outline-none text-xs" required />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Discounted Price (₹)</label>
                <div className="relative group">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-black text-gray-300 group-focus-within:text-emerald-500 transition-colors text-xs">₹</span>
                  <input name="discounted_price" type="number" value={formData.discounted_price} onChange={handleChange} placeholder="0" className="w-full pl-8 pr-4 py-3 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all font-black text-gray-900 outline-none text-xs" />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Product Description</label>
              <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Detail the features, weight, and usefulness of this product..." className="w-full px-4 py-3 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all font-medium text-gray-700 outline-none min-h-[80px] text-[10px]" required />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between px-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Inventory Visuals</label>
                <button type="button" onClick={() => setShowStockModal(true)} className="text-[10px] font-black text-emerald-500 hover:text-emerald-600 underline decoration-2 underline-offset-2 uppercase tracking-tighter">Marketplace Catalog</button>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {(formData.image_url ? (Array.isArray(formData.image_url) ? formData.image_url : formData.image_url.split(",")) : []).filter(Boolean).map((url, idx) => (
                  <div key={idx} className="relative aspect-square group rounded-[1.2rem] overflow-hidden border border-gray-100 shadow-sm">
                    <img src={url} alt="Prod" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <button type="button" onClick={() => handleRemoveImage(idx)} className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100"><Trash2 size={10} /></button>
                  </div>
                ))}
                <label className={`aspect-square rounded-[1.2rem] border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${isUploading ? 'bg-gray-50 border-gray-200' : 'bg-emerald-50/30 border-emerald-500/20 hover:bg-emerald-50/50'}`}>
                  <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" disabled={isUploading} />
                  {isUploading ? <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-emerald-500" /> : <><Upload className="text-emerald-500 mb-1" size={18} /><span className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">UPLOAD</span></>}
                </label>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4 sticky bottom-0 bg-white">
              <button type="button" onClick={onClose} className="flex-1 py-4 bg-gray-50 text-gray-500 font-black rounded-2xl hover:bg-gray-100 transition-all text-[8px] tracking-[0.2em] uppercase">Discard</button>
              <button type="submit" disabled={isUploading} className="flex-[2] py-4 bg-emerald-500 text-white font-black rounded-2xl shadow-lg shadow-emerald-500/20 hover:scale-[1.02] transition-all text-[8px] tracking-[0.2em] uppercase flex items-center justify-center gap-2">
                {editDish ? "Update Status" : "Create Listing"}<Check size={16} />
              </button>
            </div>
          </form>
        </div>
      </div>
      <StockImagesModal />
    </div>
  );

  return createPortal(content, document.body);
};

export default AddItemModal;
